import TextEllipsis, {
  TextEllipsisProps,
} from '@Components/Common/Tooltip/TextEllipsis';
import { usePermissionStore } from '@Models/usePermissionModel';
import { useUtcOffsetStore } from '@Models/useUtcOffsetModel';
import {
  Flex,
  Icon,
  LinkButton,
  Menu,
  MenuGroup,
  MenuItem,
  MenuList,
  MenuToggle,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import {
  DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF,
  DataGrid,
  DataGridProps,
} from '@tonic-one/react-data-grid';
import { MoreIcon } from '@tonic-one/react-icons';
import { PermissionNameEnum } from '@Types/permissions';
import { useNavigateWithHandler } from '@Utils/hooks';
import { handleBlockSenderCtxMenu } from '@Utils/ctxMenu';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ActionModalState, FeedbackRecordStatus } from '../../type';
import {
  formatCecpRecords,
  FormattedCecpData,
  shouldLinkToDetails,
  shouldMitigationLinkToDetails,
} from './utils';

interface CecpColumnConfig {
  order: string[];
  hideIds: string[];
  columnNames: { [columnId: string]: string };
}

interface DetailsTableCustomProps {
  sortKey: string;
  setSortKey: (key: string) => void;
  setActionModalState?: (v: ActionModalState) => void;
  columnConfig?: CecpColumnConfig;
}

interface DetailsTableProps
  extends Omit<DataGridProps, 'columns'>,
    DetailsTableCustomProps {}

export const DetailsTable = memo(
  ({
    data,
    sortKey,
    setSortKey,
    setActionModalState,
    rowSelection,
    onRowSelectionChange,
    isLoading = false,
    pagination,
    onPaginationChange,
    rowCount,
    pageSizeOptions = [25, 50, 100],
    columnConfig,
  }: DetailsTableProps) => {
    const { t } = useTranslation();
    const { offset } = useUtcOffsetStore();
    const [colorMode] = useColorMode();
    const [colorStyle] = useColorStyle({ colorMode });
    const timeFormat = t('common.momentDateFormat');
    const navigate = useNavigateWithHandler();
    const { widthEditablePermission } = usePermissionStore();
    const qtEditPermission = widthEditablePermission(
      PermissionNameEnum.quarantine
    );

    // Format data using shared utility
    const formattedData = useMemo(() => {
      return formatCecpRecords(data, offset, timeFormat, t);
    }, [data, offset, timeFormat, t]);

    const allColumnDefinitions = useMemo(() => {
      const Ellipsis = ({ children, ...rest }: TextEllipsisProps) => (
        <TextEllipsis popperProps={{ usePortal: true }} {...rest}>
          {children}
        </TextEllipsis>
      );

      const LinkToDetails = (props: {
        row: { value: FormattedCecpData };
        label: string;
      }) => {
        const { id, company_guid: companyGuid } = props.row.value;
        const { label } = props;
        return (
          <LinkButton
            onClick={() => {
              const path = `/quarantine/euq-feedback/${companyGuid}/${id}`;
              navigate(path);
            }}
            textAlign="left"
            width="100%"
          >
            <Ellipsis>{label}</Ellipsis>
          </LinkButton>
        );
      };

      return {
        // 1. Reported at
        created_at: {
          id: 'created_at',
          header: t('operations.euq.detail.table.header.reportedAt'),
          accessorKey: 'created_at',
          width: '10%',
          isSortable: true,
          cell: ({ row }: { row: { value: FormattedCecpData } }) => {
            if (shouldLinkToDetails(row.value.originalData)) {
              return <LinkToDetails row={row} label={row.value.created_at} />;
            }
            return <Ellipsis>{row.value.created_at}</Ellipsis>;
          },
        },
        // 2. Email Received
        message_arrive_time: {
          id: 'message_arrive_time',
          header: t('operations.euq.detail.table.header.arrivingTime'),
          accessorKey: 'message_arrive_time',
          width: '10%',
          isSortable: true,
          cell: ({ row }: { row: { value: FormattedCecpData } }) => (
            <Ellipsis>{row.value.message_arrive_time}</Ellipsis>
          ),
        },
        // 3. Subject
        message_subject: {
          id: 'message_subject',
          header: t('operations.euq.detail.table.header.subject'),
          accessorKey: 'message_subject',
          width: '10%',
          isSortable: true,
          cell: ({ row }: { row: { value: FormattedCecpData } }) => (
            <Ellipsis>{row.value.message_subject}</Ellipsis>
          ),
        },
        // 4. Sender
        message_sender: {
          id: 'message_sender',
          header: t('operations.euq.detail.table.header.sender'),
          accessorKey: 'message_sender',
          width: '10%',
          isSortable: true,
          cell: ({ row }: { row: { value: FormattedCecpData } }) => (
            <Ellipsis>{row.value.message_sender}</Ellipsis>
          ),
        },
        // 5. Recipient
        message_recipients: {
          id: 'message_recipients',
          header: t('operations.euq.detail.table.header.recipient'),
          accessorKey: 'message_recipients',
          width: '10%',
          isSortable: true,
          cell: ({ row }: { row: { value: FormattedCecpData } }) => (
            <Ellipsis>{row.value.message_recipients}</Ellipsis>
          ),
        },
        // 6. Reporter
        requested_by: {
          id: 'requested_by',
          header: t('operations.euq.detail.table.header.reporter'),
          accessorKey: 'requested_by',
          width: '10%',
          isSortable: true,
          cell: ({ row }: { row: { value: FormattedCecpData } }) => (
            <Ellipsis>{row.value.requested_by}</Ellipsis>
          ),
        },
        // 7. Service Type
        service_type: {
          id: 'service_type',
          header: t('operations.euq.filter.serviceType'),
          accessorKey: 'service_type',
          width: '8%',
          isSortable: true,
          cell: ({ row }: { row: { value: FormattedCecpData } }) => (
            <Ellipsis>{row.value.service_type}</Ellipsis>
          ),
        },
        // 8. Reported type
        category: {
          id: 'category',
          header: t('operations.euq.filter.reportedType'),
          accessorKey: 'category',
          width: '10%',
          isSortable: true,
          cell: ({ row }: { row: { value: FormattedCecpData } }) => (
            <Ellipsis>{row.value.category}</Ellipsis>
          ),
        },
        request_source: {
          id: 'request_source',
          header: t('administration.addIn.autoCollect.collectSource.column'),
          accessorKey: 'request_source',
          width: '10%',
          isSortable: true,
          cell: ({ row }: { row: { value: FormattedCecpData } }) => (
            <Ellipsis>{row.value.request_source}</Ellipsis>
          ),
        },
        // 9. Approval status
        approval_status: {
          id: 'approval_status',
          header: t('operations.euq.detail.table.header.approvalStatus'),
          accessorKey: 'approval_status',
          width: '10%',
          isSortable: true,
          cell: ({ row }: { row: { value: FormattedCecpData } }) => {
            return <Ellipsis>{row.value.approval_status}</Ellipsis>;
          },
        },
        // 10. Analysis
        gtoc_result_category: {
          id: 'gtoc_result_category',
          header: t('operations.euq.table.analysis'),
          accessorKey: 'gtoc_result_category',
          width: '10%',
          cell: ({ row }: { row: { value: FormattedCecpData } }) => (
            <Ellipsis>{row.value.gtoc_result_category}</Ellipsis>
          ),
        },
        // 11. Security actions
        mitigation_status: {
          id: 'mitigation_status',
          header: t('operations.euq.table.securityActions'),
          accessorKey: 'mitigation_status',
          width: 203,
          minWidth: 203,
          maxWidth: 203,
          enableResizing: false,
          isSortable: false,
          headerProps: {
            width: 203,
            position: 'sticky',
            right: 60,
            borderLeft: 2,
            borderLeftColor: colorStyle.background.fourth,
            zIndex: 10,
            sx: {
              backgroundColor: colorStyle.background.primary,
            },
          },
          cellProps: ({ row }: { row: { value: any } }) => {
            const isSelected = rowSelection && rowSelection[row.value.id];
            return {
              position: 'sticky',
              right: 60,
              borderLeft: 2,
              borderLeftColor: colorStyle.background.fourth,
              zIndex: 10,
              width: 203,
              sx: {
                backgroundColor: isSelected
                  ? colorMode === 'light'
                    ? '#eeeeee'
                    : '#2c2c2c'
                  : colorStyle.background.primary,
                '[role="row"]:hover &': {
                  backgroundColor: colorStyle.background.tertiary,
                },
              },
            };
          },
          cell: ({ row }: { row: { value: FormattedCecpData } }) => {
            if (shouldMitigationLinkToDetails(row.value.originalData)) {
              return (
                <LinkButton
                  onClick={() => {
                    const { id, company_guid: companyGuid } = row.value;
                    const path = `/quarantine/euq-feedback/${companyGuid}/${id}`;
                    navigate(path);
                  }}
                  textAlign="left"
                  width="100%"
                >
                  <Ellipsis>{row.value.mitigation_status}</Ellipsis>
                </LinkButton>
              );
            }

            return <Ellipsis>{row.value.mitigation_status}</Ellipsis>;
          },
        },
      };
    }, [
      navigate,
      t,
      colorStyle.background.fourth,
      colorStyle.background.primary,
      colorStyle.background.tertiary,
      colorMode,
      rowSelection,
    ]);

    const columns = useMemo(() => {
      const baseColumns = [];

      baseColumns.push({
        ...DATA_GRID_CHECKBOX_SELECTION_COLUMN_DEF,
        width: 40,
        minWidth: 40,
      });

      // Add data columns based on column config
      const columnOrder = columnConfig?.order || [
        'created_at',
        'message_arrive_time',
        'message_subject',
        'message_sender',
        'message_recipients',
        'requested_by',
        'service_type',
        'category',
        'request_source',
        'approval_status',
        'gtoc_result_category',
        'mitigation_status',
      ];

      const hideIds = columnConfig?.hideIds || [];

      columnOrder.forEach((columnId) => {
        if (
          !hideIds.includes(columnId) &&
          allColumnDefinitions[columnId as keyof typeof allColumnDefinitions]
        ) {
          baseColumns.push(
            allColumnDefinitions[columnId as keyof typeof allColumnDefinitions]
          );
        }
      });

      // Add action column if actions are enabled
      if (setActionModalState) {
        const ACTIONS_WIDTH = 60;
        baseColumns.push({
          id: 'actions',
          width: ACTIONS_WIDTH,
          minWidth: ACTIONS_WIDTH,
          maxWidth: ACTIONS_WIDTH,
          enableResizing: false,
          isSortable: false,
          headerProps: {
            width: ACTIONS_WIDTH,
            position: 'sticky',
            right: 0,
            zIndex: 10,
            sx: {
              backgroundColor: colorStyle.background.primary,
            },
          },
          cellProps: ({ row }: { row: { value: any } }) => {
            const isSelected = rowSelection && rowSelection[row.value.id];
            return {
              position: 'sticky',
              right: 0,
              zIndex: 10,
              width: ACTIONS_WIDTH,
              sx: {
                backgroundColor: isSelected
                  ? colorMode === 'light'
                    ? '#eeeeee'
                    : '#2c2c2c'
                  : colorStyle.background.primary,
                '[role="row"]:hover &': {
                  backgroundColor: colorStyle.background.tertiary,
                },
              },
            };
          },
          cell: ({ row }: { row: { value: FormattedCecpData } }) => {
            const {
              id,
              message_sender: sender,
              status: recordStatus,
            } = row.value.originalData;
            // Check if this specific row can be acted upon (only Created status records)
            const isCreatedStatus =
              recordStatus === FeedbackRecordStatus.CREATED;
            const menuItemStyle = {
              pl: '24px',
              disabled: !qtEditPermission,
            };
            return (
              <Flex justifyContent="start" alignItems="center">
                <Menu placement="bottom-end">
                  <MenuToggle _hover={{ color: '#578aef' }}>
                    <Icon as={MoreIcon} />
                  </MenuToggle>
                  <MenuList
                    PopperProps={{
                      modifiers: [
                        {
                          name: 'flip',
                        },
                      ],
                      usePortal: true,
                    }}
                  >
                    {isCreatedStatus && (
                      <MenuGroup
                        title={t('common.ctxMenu.ReportToTM')}
                        textTransform="uppercase"
                      >
                        <MenuItem
                          onClick={() => {
                            setActionModalState({
                              id: `${id}`,
                              name: 'approve',
                            });
                          }}
                          {...menuItemStyle}
                        >
                          {t('operations:operations.euq.action.approve.btn')}
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            setActionModalState({
                              id: `${id}`,
                              name: 'reject',
                            });
                          }}
                          {...menuItemStyle}
                        >
                          {t('operations:operations.euq.action.reject.btn')}
                        </MenuItem>
                      </MenuGroup>
                    )}
                    <MenuGroup
                      title={t('common.ctxMenu.senderAction')}
                      textTransform="uppercase"
                    >
                      <MenuItem
                        {...menuItemStyle}
                        onClick={() => {
                          handleBlockSenderCtxMenu(sender);
                        }}
                      >
                        {t('common.ctxMenu.blockSender')}
                      </MenuItem>
                    </MenuGroup>
                  </MenuList>
                </Menu>
              </Flex>
            );
          },
        });
      }

      return baseColumns;
    }, [
      columnConfig?.order,
      columnConfig?.hideIds,
      setActionModalState,
      allColumnDefinitions,
      colorStyle.background.fourth,
      colorStyle.background.primary,
      colorStyle.background.tertiary,
      rowSelection,
      colorMode,
      qtEditPermission,
      t,
    ]);

    const sorting = useMemo(() => {
      const isDesc = sortKey.startsWith('-');
      const columnId = isDesc ? sortKey.slice(1) : sortKey;
      return {
        columnId,
        direction: isDesc ? 'desc' : 'asc',
      } as const;
    }, [sortKey]);

    const handleSortingChange = useCallback(
      (newSorting: { columnId: string; direction: 'asc' | 'desc' }) => {
        if (
          sorting.columnId === 'created_at' &&
          newSorting.columnId === undefined &&
          newSorting.direction === undefined
        ) {
          setSortKey('created_at');
        } else {
          const columnId = newSorting.columnId ?? 'created_at';
          const direction = newSorting.direction ?? 'desc';
          const prefix = direction === 'desc' ? '-' : '';
          setSortKey(`${prefix}${columnId}`);
        }
      },
      [setSortKey, sorting.columnId]
    );

    return (
      <DataGrid
        data={formattedData}
        columns={columns}
        isColumnSortable={true}
        autoSorting={false}
        sorting={sorting}
        onSortingChange={handleSortingChange}
        showPagination
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        rowCount={rowCount}
        pageSizeOptions={pageSizeOptions}
        isColumnResizable={true}
        isLoading={isLoading}
        emptyContent={t('common.noData')}
        getRowId={(row) => `${row.id}`}
        rowSelection={rowSelection}
        onRowSelectionChange={onRowSelectionChange}
        disableAutoResetRowSelection
        disableAutoResetPageIndex
      />
    );
  }
);
